import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import CustomFormErrorMessage from '../../../components/CustomFormErrorMessage/CustomFormErrorMessage';
import LoginFormLayout from '../../../components/LoginFormLayout/LoginFormLayout';
import { sanitizeInput } from '../../../utilities/commonFunctions';
import { CustomRegistrationFormInput } from '../../../components';
import { LOGIN_TYPE_KEYS } from '../../../utilities/constants';

const { Text } = Typography;

const LoginForm = ({
  onLogin,
  stepType,
  setLoginType,
  handleBack,
  showBackButton,
  isDeviceRegistrationLogin = false,
  email = '',
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (stepType !== 'initial') {
      form.setFieldsValue({ email });
      return;
    }

    form.setFieldsValue({ email: undefined });
  }, [email, form, stepType]);

  const onFinish = async values => {
    try {
      isDeviceRegistrationLogin && setLoginType('device-registration-login');
      setLoading(true);
      const { email: formEmail, password } = values;
      const loginEmail = stepType !== 'initial' ? email : formEmail;
      await onLogin(
        sanitizeInput(loginEmail),
        sanitizeInput(password),
        setLoading
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error: ', error);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (stepType === 'initial') {
      return 'Continue';
    } else if (stepType === LOGIN_TYPE_KEYS.SSO) {
      return 'Continue with SSO';
    } else if (stepType === LOGIN_TYPE_KEYS.MFA) {
      return 'Send OTP';
    }

    return 'Login';
  };

  return (
    <LoginFormLayout
      onFinish={onFinish}
      form={form}
      title={'Login to RideBuddy'}
      showBackButton={showBackButton}
      onBack={handleBack}
      isMobileLogin={isDeviceRegistrationLogin}
    >
      <CustomRegistrationFormInput
        label={<Text strong>Email address</Text>}
        placeholder="e.g.: hint"
        name="email"
        readOnly={
          stepType === LOGIN_TYPE_KEYS.MFA ||
          stepType === LOGIN_TYPE_KEYS.USER_PASSWORD
        }
        rules={[
          {
            required: true,
            message: (
              <CustomFormErrorMessage message="Please input your email" />
            ),
          },
          {
            type: 'email',
            message: (
              <CustomFormErrorMessage message="Please enter a valid email address" />
            ),
          },
        ]}
      />
      {(stepType === LOGIN_TYPE_KEYS.MFA ||
        stepType === LOGIN_TYPE_KEYS.USER_PASSWORD) && (
        <>
          <CustomRegistrationFormInput
            label={<Text strong>Password</Text>}
            placeholder="••••••••"
            name="password"
            rules={[
              {
                required: true,
                message: (
                  <CustomFormErrorMessage message="Please input your password" />
                ),
              },
            ]}
            isPassword
          />
          <Form.Item>
            <Button
              type="link"
              className="btn-login-link"
              onClick={() => navigate('/forgot-password')}
            >
              Forgot password?
            </Button>
          </Form.Item>
        </>
      )}

      <Form.Item>
        <Button type="primary" htmlType="submit" block loading={loading}>
          {getButtonText()}
        </Button>
      </Form.Item>
    </LoginFormLayout>
  );
};

LoginForm.propTypes = {
  onLogin: PropTypes.func.isRequired,
  stepType: PropTypes.string,
  setLoginType: PropTypes.func,
  handleBack: PropTypes.func,
  showBackButton: PropTypes.bool,
  isDeviceRegistrationLogin: PropTypes.bool,
  email: PropTypes.string,
};

export default LoginForm;
